import type { Betreuer } from 'api/betreuer';
import type { Artikel } from 'api/artikel';
import type { ArtikelDetail } from 'api/artikelDetail';
import type { ArtikelGruppe, Einheit, Kalkulationsblatt, Lager, Land, Lieferantenformular } from 'api/lookup';
import type { ArtikelEigenschaft } from 'api/artikelEigenschaft';
import type { AraRegelung } from 'api/araRegelung';
import type { Branche } from 'api/branche';
import type { ErweiterteSuche } from 'api/erweiterteSuche';
import type { InteresseFuerProdukte } from 'api/interesseFuerProdukte';
import type { Produktgruppe } from 'api/produktgruppe';
import type { Textbaustein } from 'api/textbaustein';
import type { Produzent } from 'api/produzent';
import type { Maschinentype } from 'api/maschinentype';
import type { Skontodefinition } from 'api/skontodefinition';
import type { Kontodefinition } from 'api/kontodefinition';

export enum LookupsActionType {
    REQUEST_BETREUER = 'LOOKUP/REQUEST_BETREUER',
    RECEIVE_BETREUER = 'LOOKUP/RECEIVE_BETREUER',
    REQUEST_TEXTBAUSTEINE = 'LOOKUP/REQUEST_TEXTBAUSTEINE',
    RECEIVE_TEXTBAUSTEINE = 'LOOKUP/RECEIVE_TEXTBAUSTEINE',
    // REQUEST_ARTIKEL = 'LOOKUP/REQUEST_ARTIKEL',
    // RECEIVE_ARTIKEL = 'LOOKUP/RECEIVE_ARTIKEL',
    REQUEST_ARTIKELGRUPPEN = 'LOOKUP/REQUEST_ARTIKELGRUPPEN',
    RECEIVE_ARTIKELGRUPPEN = 'LOOKUP/RECEIVE_ARTIKELGRUPPEN',
    // REQUEST_ARTIKELDETAILS = 'LOOKUP/REQUEST_ARTIKELDETAILS',
    // RECEIVE_ARTIKELDETAILS = 'LOOKUP/RECEIVE_ARTIKELDETAILS',
    REQUEST_BRANCHEN = 'LOOKUP/REQUEST_BRANCHEN',
    RECEIVE_BRANCHEN = 'LOOKUP/RECEIVE_BRANCHEN',
    REQUEST_INTERESSEFUERPRODUKTE = 'LOOKUP/REQUEST_INTERESSEFUERPRODUKTE',
    RECEIVE_INTERESSEFUERPRODUKTE = 'LOOKUP/RECEIVE_INTERESSEFUERPRODUKTE',
    REQUEST_ERWEITERTESUCHEN = 'LOOKUP/REQUEST_ERWEITERTESUCHEN',
    RECEIVE_ERWEITERTESUCHEN = 'LOOKUP/RECEIVE_ERWEITERTESUCHEN',
    REQUEST_LAENDER = 'LOOKUP/REQUEST_LAENDER',
    RECEIVE_LAENDER = 'LOOKUP/RECEIVE_LAENDER',
    REQUEST_SKONTODEFINITIONEN = 'LOOKUP/REQUEST_SKONTODEFINITIONEN',
    RECEIVE_SKONTODEFINITIONEN = 'LOOKUP/RECEIVE_SKONTODEFINITIONEN',
    REQUEST_KONTODEFINITIONEN = 'LOOKUP/REQUEST_KONTODEFINITIONEN',
    RECEIVE_KONTODEFINITIONEN = 'LOOKUP/RECEIVE_KONTODEFINITIONEN',
    REQUEST_EINHEITEN = 'LOOKUP/REQUEST_EINHEITEN',
    RECEIVE_EINHEITEN = 'LOOKUP/RECEIVE_EINHEITEN',
    REQUEST_LAGER = 'LOOKUP/REQUEST_LAGER',
    RECEIVE_LAGER = 'LOOKUP/RECEIVE_LAGER',
    REQUEST_ARA_REGELUNGEN = 'LOOKUP/REQUEST_ARA_REGELUNGEN',
    RECEIVE_ARA_REGELUNGEN = 'LOOKUP/RECEIVE_ARA_REGELUNGEN',
    REQUEST_LIEFERANTENFORMULARE = 'REQUEST_LIEFERANTENFORMULARE',
    RECEIVE_LIEFERANTENFORMULARE = 'RECEIVE_LIEFERANTENFORMULARE',
    REQUEST_KALKULATIONSBLAETTER = 'REQUEST_KALKULATIONSBLAETTER',
    RECEIVE_KALKULATIONSBLAETTER = 'RECEIVE_KALKULATIONSBLAETTER',
    REQUEST_ARTIKELEIGENSCHAFTEN = 'REQUEST_ARTIKELEIGENSCHAFTEN',
    RECEIVE_ARTIKELEIGENSCHAFTEN = 'RECEIVE_ARTIKELEIGENSCHAFTEN',
    REQUEST_PRODUKTGRUPPEN = 'REQUEST_PRODUKTGRUPPEN',
    RECEIVE_PRODUKTGRUPPEN = 'RECEIVE_PRODUKTGRUPPEN',
    REQUEST_PRODUZENTEN = 'REQUEST_PRODUZENTEN',
    RECEIVE_PRODUZENTEN = 'RECEIVE_PRODUZENTEN',
    REQUEST_MASCHINENTYPEN = 'REQUEST_MASCHINENTYPEN',
    RECEIVE_MASCHINENTYPEN = 'RECEIVE_MASCHINENTYPEN',
    FINISH_LOADING = 'LOOKUP/FINISH_LOADING',
}

export interface LookupsActionPayload {
    success?: boolean;
}

export interface BetreuerActionPayload extends LookupsActionPayload {
    betreuer: Betreuer[];
}

export interface TextbausteineActionPayload extends LookupsActionPayload {
    textbausteine: Textbaustein[];
}

export interface ArtikelActionPayload extends LookupsActionPayload {
    artikel: Artikel[];
}

export interface ArtikelGruppenActionPayload extends LookupsActionPayload {
    artikelGruppen: ArtikelGruppe[];
}

export interface ArtikelDetailActionPayload extends LookupsActionPayload {
    artikelDetails: ArtikelDetail[];
}

export interface BranchenActionPayload extends LookupsActionPayload {
    branchen: Branche[];
}

export interface InteresseFuerProdukteActionPayload extends LookupsActionPayload {
    interesseFuerProdukte: InteresseFuerProdukte[];
}

export interface ErweiterteSuchenActionPayload extends LookupsActionPayload {
    erweiterteSuchen: ErweiterteSuche[];
}

export interface LaenderActionPayload extends LookupsActionPayload {
    laender: Land[];
}

export interface SkontodefinitionenActionPayload extends LookupsActionPayload {
    skontodefinitionen: Skontodefinition[];
}

export interface KontodefinitionenActionPayload extends LookupsActionPayload {
    kontodefinitionen: Kontodefinition[];
}

export interface EinheitenActionPayload extends LookupsActionPayload {
    einheiten: Einheit[];
}

export interface LagerActionPayload extends LookupsActionPayload {
    lager: Lager[];
}

export interface AraRegelungenActionPayload extends LookupsActionPayload {
    araRegelungen: AraRegelung[];
}

export interface LieferantenformulareActionPayload extends LookupsActionPayload {
    lieferantenformulare: Lieferantenformular[];
}

export interface KalkulationsblaetterActionPayload extends LookupsActionPayload {
    kalkulationsblaetter: Kalkulationsblatt[];
}

export interface ArtikelEigenschaftenActionPayload extends LookupsActionPayload {
    artikelEigenschaften: ArtikelEigenschaft[];
}

export interface ProduktgruppenActionPayload extends LookupsActionPayload {
    produktgruppen: Produktgruppe[];
}

export interface ProduzentenActionPayload extends LookupsActionPayload {
    produzenten: Produzent[];
}

export interface MaschinentypenActionPayload extends LookupsActionPayload {
    maschinentypen: Maschinentype[];
}

export interface LookupsAction {
    type: LookupsActionType;
    payload?: LookupsActionPayload;
}

export interface Lookups {
    betreuer?: Betreuer[];
    betreuerByNr?: { [key: string]: Betreuer };
    betreuerByKz?: { [key: string]: Betreuer };
    betreuerLoaded: boolean;
    textbausteine?: Textbaustein[];
    textbausteineByKz?: { [key: number]: Textbaustein[] };
    textbausteineByKzNr?: { [key: number]: { [key: number]: Textbaustein } };
    textbausteineLoaded: boolean;
    artikelGruppen?: ArtikelGruppe[];
    artikelGruppenByNr?: { [key: number]: ArtikelGruppe };
    artikelGruppenLoaded: boolean;
    branchen?: Branche[];
    branchenByNr?: { [key: string]: Branche };
    branchenLoaded: boolean;
    interesseFuerProdukte?: InteresseFuerProdukte[];
    interesseFuerProdukteByNr?: { [key: string]: InteresseFuerProdukte };
    interesseFuerProdukteLoaded: boolean;
    erweiterteSuchen?: ErweiterteSuche[];
    erweiterteSuchenByNr?: { [key: string]: ErweiterteSuche };
    erweiterteSuchenLoaded: boolean;
    laender?: Land[];
    laenderByIkz2?: { [key: string]: Land };
    laenderLoaded: boolean;
    skontodefinitionen?: Skontodefinition[];
    skontodefinitionenByArt?: { [key: number]: Skontodefinition };
    skontodefinitionenLoaded: boolean;
    kontodefinitionen?: Kontodefinition[];
    kontodefinitionenLoaded: boolean;
    einheiten?: Einheit[];
    einheitenByCode?: { [key: string]: Einheit };
    einheitenLoaded: boolean;
    lager?: Lager[];
    lagerByIntNr?: { [key: number]: Lager };
    lagerLoaded: boolean;
    araRegelungen?: AraRegelung[];
    araRegelungenByArt?: { [key: number]: AraRegelung };
    araRegelungenLoaded: boolean;
    lieferantenformulare?: Lieferantenformular[];
    lieferantenformulareByNr?: { [key: number]: Lieferantenformular };
    lieferantenformulareLoaded: boolean;
    kalkulationsblaetter?: Kalkulationsblatt[];
    kalkulationsblaetterByBlatt?: { [key: string]: Kalkulationsblatt };
    kalkulationsblaetterLoaded: boolean;
    artikelEigenschaften?: ArtikelEigenschaft[];
    artikelEigenschaftenByKzNr?: { [key: string]: { [key: number]: ArtikelEigenschaft } };
    artikelEigenschaftenLoaded: boolean;
    produktgruppen?: Produktgruppe[];
    produktgruppenByNr?: { [key: number]: Produktgruppe };
    produktgruppenLoaded: boolean;
    produzenten?: Produzent[];
    produzentenByNr?: { [key: number]: Produzent };
    produzentenLoaded: boolean;
    maschinentypen?: Maschinentype[];
    maschinentypenByNr?: { [key: number]: Maschinentype };
    maschinentypenLoaded: boolean;

    loadingFinished: boolean;
}

export const defaultLookups: Lookups = {
    betreuer: [],
    betreuerByNr: {},
    betreuerByKz: {},
    betreuerLoaded: false,
    textbausteine: [],
    textbausteineByKzNr: {},
    textbausteineLoaded: false,
    artikelGruppen: [],
    artikelGruppenByNr: {},
    artikelGruppenLoaded: false,
    branchen: [],
    branchenByNr: {},
    branchenLoaded: false,
    interesseFuerProdukte: [],
    interesseFuerProdukteByNr: {},
    interesseFuerProdukteLoaded: false,
    erweiterteSuchen: [],
    erweiterteSuchenByNr: {},
    erweiterteSuchenLoaded: false,
    laender: [],
    laenderByIkz2: {},
    laenderLoaded: false,
    skontodefinitionen: [],
    skontodefinitionenByArt: {},
    skontodefinitionenLoaded: false,
    kontodefinitionen: [],
    kontodefinitionenLoaded: false,
    einheiten: [],
    einheitenByCode: {},
    einheitenLoaded: false,
    lager: [],
    lagerByIntNr: {},
    lagerLoaded: false,
    araRegelungen: [],
    araRegelungenByArt: {},
    araRegelungenLoaded: false,
    lieferantenformulare: [],
    lieferantenformulareByNr: {},
    lieferantenformulareLoaded: false,
    kalkulationsblaetter: [],
    kalkulationsblaetterByBlatt: {},
    kalkulationsblaetterLoaded: false,
    artikelEigenschaften: [],
    artikelEigenschaftenByKzNr: {},
    artikelEigenschaftenLoaded: false,
    produktgruppen: [],
    produktgruppenByNr: {},
    produktgruppenLoaded: false,
    produzenten: [],
    produzentenByNr: {},
    produzentenLoaded: false,
    maschinentypen: [],
    maschinentypenByNr: {},
    maschinentypenLoaded: false,

    loadingFinished: false,
};
